import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { parseTourSlug } from '@/lib/tourHelpers';

export interface TourPlace {
  name: string;
  city: string;
  country: string;
  image: string | null;
  description_fr: string | null;
  description_en: string | null;
  visit_tips_fr: string | null;
  visit_tips_en: string | null;
  latitude: number | null;
  longitude: number | null;
  site_officiel: string | null;
  prix: string | null;
  prix_en: string | null;
  heures: string | null;
}

export interface TourDetails {
  id: string;
  title: string;
  city: string;
  country: string;
  region: string;
  style: string;
  duration: string;
  description: string | null;
  image: string | null;
  museum_count: number;
  latitude: number | null;
  longitude: number | null;
  places: TourPlace[];
  created_at: string;
}

export const useTourDetails = (tourSlug: string | undefined) => {
  const [tour, setTour] = useState<TourDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTourDetails = async () => {
      if (!tourSlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Parse the slug to get style and city
        const parsed = parseTourSlug(tourSlug);
        if (!parsed) {
          setError('Invalid tour slug format');
          setLoading(false);
          return;
        }

        // Fetch tour from city_tours matching style and city
        // Use exact match on city name (case-insensitive)
        const { data: tourData, error: tourError } = await supabase
          .from('city_tours')
          .select('*')
          .eq('style', parsed.style)
          .eq('is_public', true);

        if (tourError) throw tourError;
        
        // Filter by city name (case-insensitive and normalized)
        const normalizeCity = (city: string) => city.toLowerCase().trim().replace(/\s+/g, ' ');
        const matchingTour = tourData?.find(
          t => normalizeCity(t['Nom ville'] || '') === normalizeCity(parsed.city)
        );
        
        if (!matchingTour) {
          setError('Tour not found');
          setLoading(false);
          return;
        }

        // Extract place names from the tour
        let placeNames: string[] = [];
        if (matchingTour.name_places) {
          if (Array.isArray(matchingTour.name_places)) {
            placeNames = matchingTour.name_places.filter((name): name is string => typeof name === 'string');
          } else if (typeof matchingTour.name_places === 'object') {
            placeNames = Object.values(matchingTour.name_places).filter(
              (name): name is string => typeof name === 'string'
            );
          }
        }

        // Fetch all places data from podcasts table
        let places: TourPlace[] = [];
        if (placeNames.length > 0) {
          const { data: placesData, error: placesError } = await supabase
            .from('podcasts')
            .select('Name, City, Country, Image, Description_FR, Description_EN, Conseils_visite, Conseils_visite_en, Latitude, Longitude, Site_officiel, Prix, Prix_en, Heures')
            .in('Name', placeNames);

          if (placesError) {
            console.error('Error fetching places:', placesError);
          } else if (placesData) {
            // Sort places in the same order as name_places
            const placesMap = new Map(placesData.map(p => [p.Name, p]));
            places = placeNames
              .map(name => placesMap.get(name))
              .filter((p): p is typeof placesData[0] => p !== undefined)
              .map(p => ({
                name: p.Name,
                city: p.City,
                country: p.Country,
                image: p.Image,
                description_fr: p.Description_FR,
                description_en: p.Description_EN,
                visit_tips_fr: p.Conseils_visite,
                visit_tips_en: p.Conseils_visite_en,
                latitude: p.Latitude,
                longitude: p.Longitude,
                site_officiel: p.Site_officiel,
                prix: p.Prix,
                prix_en: p.Prix_en,
                heures: p.Heures,
              }));
          }
        }

        // Construct the tour details
        const tourDetails: TourDetails = {
          id: matchingTour.id,
          title: matchingTour['Nom ville'],
          city: matchingTour['Nom ville'],
          country: matchingTour.country,
          region: matchingTour.Region,
          style: matchingTour.style,
          duration: matchingTour.duration,
          description: matchingTour.description,
          image: matchingTour.Image,
          museum_count: matchingTour.museum_count,
          latitude: matchingTour.latitude,
          longitude: matchingTour.longitude,
          places,
          created_at: matchingTour.created_at,
        };

        setTour(tourDetails);
        setError(null);
      } catch (err) {
        console.error('Error fetching tour details:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchTourDetails();
  }, [tourSlug]);

  return { tour, loading, error };
};
